import { useState, useCallback, useEffect } from 'react';
import { message, Modal } from 'antd';
import { User, UserCreateDTO, UserStatus, UserUpdateDTO } from '../types/user.types';
import userService from '../services/user/user.service';
import departmentService from '../services/department/department.service';
import shopService from '../services/shop/shop.service';
import { DepartmentDto, DepartmentStatus } from '../types/department.types';
import { Store } from '../types/shop.types';
import { useAsync } from './useAsync';

export const useEmployee = () => {
  const { data: users, loading, error, execute } = useAsync<User[]>();
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [shops, setShops] = useState<Store[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedShop, setSelectedShop] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<UserStatus | string>('all');
  const [selectedBloodType, setSelectedBloodType] = useState<string>('all');
  useEffect(() => {
    if (users) {
      setFilteredUsers(users);
    }
  }, [users]);

  const fetchUsers = useCallback(async () => {
    return execute(() => userService.getUsers());
  }, [execute]);

  const fetchDepartmentsAndShops = useCallback(async () => {
    try {
      const [depsResult, shopsResult] = await Promise.all([
        departmentService.getDepartments(),
        shopService.getShops()
      ]);
      setDepartments(depsResult.filter(dep => dep.status === DepartmentStatus.ACTIVE));
      setShops(shopsResult);
    } catch (err: any) {
      message.error("Veriler yüklenirken bir hata oluştu");
    }
  }, []);


  const handleStatusChange = (status: UserStatus | string) => {
    setSelectedStatus(status);
    filterUsers(selectedDepartment, selectedShop, status, selectedBloodType);
  };

  const handleDepartmentChange = (departmentId: string) => {
    setSelectedDepartment(departmentId);
    filterUsers(departmentId, selectedShop, selectedStatus, selectedBloodType);
  };

  const handleShopChange = (shopId: string) => {
    setSelectedShop(shopId);
    filterUsers(selectedDepartment, shopId, selectedStatus, selectedBloodType);
  };

  const handleBloodTypeChange = (bloodType: string) => {
    setSelectedBloodType(bloodType);
    filterUsers(selectedDepartment, selectedShop, selectedStatus, bloodType);
  };

  const filterUsers = (departmentId: string, shopId: string, status: UserStatus | string, bloodType: string) => {
    if (!users) return;
    let filtered = [...users];

    if (departmentId !== 'all') {
      filtered = filtered.filter(user => user.department?.map(d => d.id).includes(Number(departmentId)));
    }

    if (shopId !== 'all') {
      filtered = filtered.filter(user => user.branch?.map(b => b.id).includes(Number(shopId)));
    }

    if (status !== 'all') {
      filtered = filtered.filter(user => user.status === status);
    }

    if (bloodType !== 'all') {
      filtered = filtered.filter(user => user.bloodType === bloodType);
    }

    setFilteredUsers(filtered);
  };

  const resetFilters = () => {
    setSelectedDepartment('all');
    setSelectedShop('all');
    setSelectedStatus('all');
    setSelectedBloodType('all');
    if (!users) return;
    setFilteredUsers(users);
  };

  const createEmployee = async (data: UserCreateDTO) => {
    try {
      await userService.createUser(data);
      message.success('Çalışan başarıyla eklendi');
      await fetchUsers();
    } catch (err: any) {
      // Sabit/anlamsız mesaj göstermiyoruz — çağıran taraf (handleFormSubmit →
      // handleApiError) backend'in gerçek hata mesajını zaten gösteriyor.
      // İkisini birden gösterirsek gerçek mesaj anlamsız olanın altında kayboluyordu.
      throw err;
    }
  };

  const updateEmployee = async (id: number, data: UserUpdateDTO) => {
    try {
      await userService.updateUserAdmin(id, data);
      message.success('Çalışan başarıyla güncellendi');
      await fetchUsers();
    } catch (err: any) {
      throw err;
    }
  };

  const updateEmployeeStatus = async (id: number, status: UserStatus) => {
    try {
      await userService.updateUserStatus(id, status);
      message.success('Çalışan durumu başarıyla güncellendi');
      await fetchUsers();
    } catch (err: any) {
      // Zimmet hatası varsa özel mesaj göster
      if (err.message && err.message.includes('iade edilmemiş zimmeti')) {
        Modal.error({
          title: 'İşten Çıkarma Engellendi',
          content: err.message,
          width: 500,
          okText: 'Tamam',
          autoFocusButton: 'ok',
        });
      } else {
        message.error("Çalışan durumu güncellenirken bir hata oluştu");
      }
      throw err;
    }
  };

  const deleteEmployee = async (id: number) => {
    try {
      await userService.deleteUser(id);
      message.success('Çalışan başarıyla silindi');
      await fetchUsers();
    } catch (err: any) {
      message.error("Çalışan silinirken bir hata oluştu");
      throw err;
    }
  };

  return {
    users: filteredUsers,
    departments,
    shops,
    loading,
    error,
    selectedDepartment,
    selectedShop,
    selectedStatus,
    selectedBloodType,
    handleDepartmentChange,
    handleShopChange,
    handleBloodTypeChange,
    resetFilters,
    fetchUsers,
    fetchDepartmentsAndShops,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    updateEmployeeStatus,
    handleStatusChange
  };
}; 